import { Navigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { Breadcrumbs } from '@/features/omega/Breadcrumbs';
import { apiFetch } from '@/api/http';

// ── Types ────────────────────────────────────────────────────────────────────

interface CompanyResponse {
  company_id: string;
  name: string;
  sector: string;
  workers: number;
  royalty_rate_bps: number;
  status: string;
  created_at: string;
}

interface PortfolioResponse {
  companies: CompanyResponse[];
  total: number;
}

interface SpawnProposalsResponse {
  proposals: unknown[];
  total: number;
}

interface SectorSignalsResponse {
  signals: unknown[];
  total: number;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

export function fmtCents(cents: number): string { return '$' + (cents / 100).toFixed(2); }
export function fmtBps(bps: number): string { return (bps / 100).toFixed(2) + '%'; }
export function fmtTs(ts: number): string {
  return new Date(ts * 1000).toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric' });
}
export function proposalStatusColor(status: string): string {
  switch (status) {
    case 'pending':  return '#fbbf24';
    case 'approved': return '#34d399';
    case 'rejected': return '#f87171';
    case 'active':   return '#34d399';
    case 'inactive': return '#94a3b8';
    default:         return 'var(--omega-ink)';
  }
}

// ── Sub-components ────────────────────────────────────────────────────────────

function StatCard({ label, value, color, highlight }: {
  label: string; value: string | number; color?: string; highlight?: boolean;
}) {
  return (
    <div style={{
      background: highlight ? 'rgba(125,211,252,0.08)' : 'var(--omega-surface, rgba(255,255,255,0.04))',
      border: highlight ? '1px solid rgba(125,211,252,0.4)' : '1px solid var(--omega-rim, rgba(255,255,255,0.08))',
      borderRadius: 6, padding: '14px 18px', minWidth: 140,
    }}>
      <div style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: '18pt', fontWeight: 700, color: color ?? 'var(--omega-ink)' }}>{value}</div>
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function AlphaOmegaDashboardPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const { data: portfolio } = useQuery({
    queryKey: ['alpha-omega', 'portfolio'],
    queryFn: () => apiFetch<PortfolioResponse>('/alpha-omega/portfolio'),
    refetchInterval: 30_000,
  });

  const { data: proposals } = useQuery({
    queryKey: ['alpha-omega', 'spawn-proposals', 'pending'],
    queryFn: () => apiFetch<SpawnProposalsResponse>('/alpha-omega/spawn-proposals?status=pending'),
    refetchInterval: 30_000,
  });

  const { data: signals } = useQuery({
    queryKey: ['alpha-omega', 'sector-signals'],
    queryFn: () => apiFetch<SectorSignalsResponse>('/alpha-omega/sector-signals'),
    refetchInterval: 30_000,
  });

  const totalWorkers = portfolio?.companies.reduce((s, c) => s + c.workers, 0) ?? 0;
  const pendingProposals = proposals?.total ?? 0;

  const navLinks = [
    { to: '/omega/alpha-omega/approve',          label: 'Approval queue' },
    { to: '/omega/alpha-omega/sector-signals',   label: 'Market signals' },
    { to: '/omega/alpha-omega/spawn-proposals',  label: 'Proposals spawn' },
    { to: '/omega/alpha-omega/monthly-pl',       label: 'Monthly CPA' },
    { to: '/omega/alpha-omega/royalty-tracking', label: 'Royalties' },
    { to: '/omega/alpha-omega/worker-allocation',label: 'Workers' },
  ];

  const navLinkStyle: React.CSSProperties = {
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid var(--omega-rim, rgba(255,255,255,0.1))',
    borderRadius: 4, color: 'var(--omega-ink)', fontFamily: 'var(--omega-mono)',
    fontSize: '8.5pt', fontWeight: 600, padding: '6px 14px',
    textDecoration: 'none', letterSpacing: '0.04em',
  };

  return (
    <div className="omega-shell" style={{ fontFamily: 'var(--omega-sans)', color: 'var(--omega-ink)', padding: '16px 20px' }}>
      <Breadcrumbs crumbs={[{ label: 'OMEGA', to: '/omega' }, { label: 'Alpha-Omega' }]} />

      <div style={{ marginBottom: 20, marginTop: 8 }}>
        <h1 style={{ margin: 0, fontSize: '12pt', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          Alpha-Omega Brain
        </h1>
        <div style={{ fontSize: '9pt', color: 'var(--omega-ink-muted)', marginTop: 4, fontFamily: 'var(--omega-mono)' }}>
          Portfolio companies & management spawn
        </div>
      </div>

      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 28 }}>
        <StatCard label="Portfolio companies" value={portfolio?.total ?? '—'} highlight />
        <StatCard label="Active workers" value={portfolio ? totalWorkers : '—'} />
        <StatCard
          label="Pending proposals"
          value={pendingProposals}
          color={pendingProposals > 0 ? '#fbbf24' : 'var(--omega-ink)'}
          highlight={pendingProposals > 0}
        />
        <StatCard label="This week's signals" value={signals?.total ?? '—'} />
      </div>

      {portfolio && portfolio.companies.length > 0 && (
        <section style={{ marginBottom: 28 }}>
          <div style={{ fontSize: '9pt', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--omega-ink)', marginBottom: 10, fontFamily: 'var(--omega-mono)' }}>
            Companies
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxWidth: 720 }}>
            {portfolio.companies.map(c => (
              <Link
                key={c.company_id}
                to={`/omega/alpha-omega/portfolio/${c.company_id}`}
                style={{
                  display: 'grid', gridTemplateColumns: '1fr 110px 90px 80px',
                  gap: 12, padding: '10px 14px',
                  background: 'rgba(255,255,255,0.02)',
                  border: '1px solid var(--omega-rim, rgba(255,255,255,0.08))',
                  borderRadius: 4, textDecoration: 'none', color: 'var(--omega-ink)',
                }}
              >
                <span style={{ fontSize: '9pt', fontWeight: 600 }}>{c.name}</span>
                <span style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', fontFamily: 'var(--omega-mono)' }}>{c.sector}</span>
                <span style={{ fontSize: '8.5pt', fontFamily: 'var(--omega-mono)' }}>{c.workers} Workers</span>
                <span style={{ fontSize: '8.5pt', color: proposalStatusColor(c.status), fontFamily: 'var(--omega-mono)', textTransform: 'uppercase' }}>{c.status}</span>
              </Link>
            ))}
          </div>
        </section>
      )}

      <section>
        <div style={{ fontSize: '9pt', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--omega-ink)', marginBottom: 10, fontFamily: 'var(--omega-mono)' }}>
          Tools
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 10, maxWidth: 700 }}>
          {navLinks.map(({ to, label }) => (
            <Link key={to} to={to} style={navLinkStyle}>{label}</Link>
          ))}
        </div>
      </section>
    </div>
  );
}
